import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { Permission, Viewer } from '@sparkery/contracts';
import { can } from '@sparkery/contracts';
import { PortalShell } from './shell/PortalShell.tsx';
import { DashboardView } from './dashboard/DashboardView.tsx';
import { ReportingView } from './reporting/ReportingView.tsx';
import { ReferView } from './refer/ReferView.tsx';
import { AssetFolderView, AssetsView } from './assets/AssetsView.tsx';
import { StatementsView } from './statements/StatementsView.tsx';
import { SettingsView } from './settings/SettingsView.tsx';
import { TeamView } from './team/TeamView.tsx';
import { ProfileView } from './profile/ProfileView.tsx';
import { PartnersView } from './partners/PartnersView.tsx';
import { isPartnerDetailTab, PartnerDetailView } from './partners/PartnerDetailView.tsx';
import { ForbiddenView } from './ForbiddenView.tsx';

export interface RouterContext {
  viewer: Viewer;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: PortalShell,
});

/**
 * A route the viewer must hold a permission to reach.
 *
 * This is a client-side check for *navigation*, not protection — the API's guard is
 * what actually protects data. Its job is to stop a viewer landing on a page whose
 * every request would 403, which reads as a broken app rather than a boundary.
 *
 * Redirecting to /forbidden rather than throwing keeps the shell mounted, so the
 * viewer can still navigate somewhere they are allowed.
 */
function guarded<TPath extends string>(config: {
  // Generic over the path so the literal type survives the factory. Without it every
  // route collapses to `string`, the router's `to` union loses these paths, and
  // type-safe navigation silently stops working.
  path: TPath;
  permission: Permission;
  component: () => ReactNode;
}) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path: config.path,
    beforeLoad: ({ context }) => {
      if (!can(context.viewer, config.permission)) {
        throw redirect({ to: '/forbidden', search: { from: config.path } });
      }
    },
    component: config.component,
  });
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

const dashboardRoute = guarded({
  path: '/dashboard',
  permission: 'dashboard:read',
  component: DashboardView,
});

const reportingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reporting',
  // Staff arrive here from a row in Partners. A partner user may set it too and be
  // ignored — the server pins them to their own partner regardless.
  validateSearch: (search: Record<string, unknown>) => ({
    partnerId: typeof search['partnerId'] === 'string' ? search['partnerId'] : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!can(context.viewer, 'reporting:read')) {
      throw redirect({ to: '/forbidden', search: { from: '/reporting' } });
    }
  },
  component: ReportingView,
});

const referRoute = guarded({
  path: '/refer',
  permission: 'links:read',
  component: ReferView,
});

const assetsRoute = guarded({
  path: '/assets',
  permission: 'assets:read',
  component: AssetsView,
});

const assetFolderRoute = guarded({
  path: '/assets/$folderId',
  permission: 'assets:read',
  component: AssetFolderView,
});

const statementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/statements',
  /**
   * `?open=<id>` is which statement is expanded.
   *
   * In the URL rather than in component state so a support conversation can link
   * straight to the month being queried — which is the whole reason the drill-down
   * exists.
   */
  validateSearch: (search: Record<string, unknown>) => ({
    open: typeof search['open'] === 'string' ? search['open'] : undefined,
  }),
  beforeLoad: ({ context }) => {
    // Guarded on `statements:read`, which a fixed-fee partner is never issued. So this
    // route redirects for them, the nav item is absent, and the endpoint refuses them —
    // one decision, made when the seat is built, rather than three places hiding it.
    if (!can(context.viewer, 'statements:read')) {
      throw redirect({ to: '/forbidden', search: { from: '/statements' } });
    }
  },
  component: StatementsView,
});

const teamRoute = guarded({
  path: '/team',
  permission: 'team:read',
  component: TeamView,
});

const profileRoute = guarded({
  path: '/profile',
  permission: 'profile:manage',
  component: ProfileView,
});

const partnersRoute = guarded({
  path: '/admin/partners',
  permission: 'partners:read',
  component: PartnersView,
});

const partnerDetailIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/partners/$partnerId',
  beforeLoad: ({ context, params }) => {
    // partners:read to look; the POST inside is guarded separately by partners:manage,
    // so a read-only staff role sees the mapping without being able to change it.
    if (!can(context.viewer, 'partners:read')) {
      throw redirect({ to: '/forbidden', search: { from: '/admin/partners' } });
    }
    throw redirect({
      to: '/admin/partners/$partnerId/$tab',
      params: { partnerId: params.partnerId, tab: 'details' },
    });
  },
});

const partnerDetailTabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/partners/$partnerId/$tab',
  beforeLoad: ({ context, params }) => {
    if (!can(context.viewer, 'partners:read')) {
      throw redirect({ to: '/forbidden', search: { from: '/admin/partners' } });
    }
    if (!isPartnerDetailTab(params.tab)) {
      throw redirect({
        to: '/admin/partners/$partnerId/$tab',
        params: { partnerId: params.partnerId, tab: 'details' },
      });
    }
  },
  component: PartnerDetailView,
});

/**
 * Programme settings, under `partners:manage`.
 *
 * The same permission as the partner writes, because these values decide the same
 * things at programme scale: which orders are referrals, and how long commission waits.
 */
const settingsRoute = guarded({
  path: '/admin/settings',
  permission: 'partners:manage',
  component: SettingsView,
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forbidden',
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search['from'] === 'string' ? search['from'] : undefined,
  }),
  component: ForbiddenView,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  dashboardRoute,
  reportingRoute,
  referRoute,
  assetsRoute,
  assetFolderRoute,
  statementsRoute,
  teamRoute,
  profileRoute,
  partnersRoute,
  partnerDetailIndexRoute,
  partnerDetailTabRoute,
  settingsRoute,
  forbiddenRoute,
]);

export function buildRouter(viewer: Viewer) {
  return createRouter({
    routeTree,
    context: { viewer },
    defaultPreload: 'intent',
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
